import { useState, useEffect } from 'react';
import { Download, Eye, DollarSign, FileText, Filter } from 'lucide-react';
import { payslipService, payrollRunService } from '../api/mockRepo';
import { downloadPayslipPDF } from '../utils/payslipPdf';
import type { Payslip, PayrollRun } from '../types';
import toast from 'react-hot-toast';

export default function PayslipsListPage() {
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterPayslips();
  }, [selectedRun, selectedStatus]);

  const loadData = async () => {
    try {
      const [payslipsData, runsData] = await Promise.all([
        payslipService.getAll(),
        payrollRunService.getAll()
      ]);
      setPayslips(payslipsData);
      setRuns(runsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load payslips');
    } finally {
      setLoading(false);
    }
  };

  const filterPayslips = async () => {
    try {
      let filteredPayslips = await payslipService.getAll();
      
      if (selectedRun) {
        filteredPayslips = await payslipService.getByRunId(selectedRun);
      }
      
      if (selectedStatus) {
        filteredPayslips = filteredPayslips.filter(slip => slip.status === selectedStatus);
      }
      
      setPayslips(filteredPayslips);
    } catch (error) {
      console.error('Failed to filter payslips:', error);
    }
  };

  const handleGeneratePayslipPDF = (payslip: Payslip) => {
    try {
      downloadPayslipPDF(payslip);
      toast.success(`PDF generated for ${payslip.employee_name}`);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleApprovePayslip = async (payslipId: string) => {
    try {
      await payslipService.updateStatus(payslipId, 'approved');
      await filterPayslips();
      toast.success('Payslip approved');
    } catch (error: any) {
      toast.error(error.message || 'Failed to approve payslip');
    }
  };

  const getStatusColor = (status: Payslip['status']) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'paid': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payslips</h1>
          <p className="text-sm text-neutral-600">View and manage employee payslips</p>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Payslips</p>
              <p className="text-2xl font-bold text-neutral-900">{payslips.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Draft</p>
              <p className="text-2xl font-bold text-neutral-900">
                {payslips.filter(p => p.status === 'draft').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Approved</p>
              <p className="text-2xl font-bold text-neutral-900">
                {payslips.filter(p => p.status === 'approved').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-emerald-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Net Pay</p>
              <p className="text-2xl font-bold text-neutral-900">
                ${payslips.reduce((sum, p) => sum + p.net_salary, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payroll Run
              </label>
              <select
                value={selectedRun}
                onChange={(e) => setSelectedRun(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All runs</option>
                {runs.map(run => (
                  <option key={run.id} value={run.id}>
                    {run.period_start} to {run.period_end}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Payslips Table */}
      <div className="bg-white rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gross Pay
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deductions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net Pay
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payslips.map((payslip) => (
                <tr key={payslip.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {payslip.employee_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {payslip.employee_code} • {payslip.department}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {payslip.period_start} to {payslip.period_end}
                    </div>
                    <div className="text-sm text-gray-500">
                      {payslip.present_days}/{payslip.working_days} days
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${payslip.gross_earnings.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${payslip.total_deductions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${payslip.net_salary.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payslip.status)}`}>
                      {payslip.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleGeneratePayslipPDF(payslip)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Generate PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => window.open(`/payroll/slips/${payslip.id}`, '_blank')}
                        className="text-gray-600 hover:text-gray-900"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      {payslip.status === 'draft' && (
                        <button
                          onClick={() => handleApprovePayslip(payslip.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Approve"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {payslips.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No payslips found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create a payroll run to generate payslips.
          </p>
        </div>
      )}
    </div>
  );
}
